/* eslint-disable sonarjs/cognitive-complexity */
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import cx from 'classnames';

import { RootState } from 'store';
import {
  getResellerMediaOwnersCreativeStatus,
  type ResellerMediaOwnersCreativeStatusResponse,
} from 'modules/api/reseller';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { formatDateTimeNoUTC } from 'modules/I18N';

import Chip, { Color, Size } from 'lib/Chip';
import chevron_down from 'assets/icons/arrow_light_down.svg';
import clockSvg from 'assets/icons/clock.svg';
import checkmark from 'assets/icons/check.svg';
import crossSvg from 'assets/icons/close.svg';
import flagSvg from 'assets/icons/flag.svg';

const SUMMARY_CREATIVE_STATUSES = [CreativeStatus.PENDING, CreativeStatus.APPROVED, CreativeStatus.REJECTED] as const;
type SummaryCreativeStatus = Extract<CreativeStatus, (typeof SUMMARY_CREATIVE_STATUSES)[number]>;

const AVAILABLE_CREATIVE_STATUSES = [...SUMMARY_CREATIVE_STATUSES, CreativeStatus.FLAGGED] as const;
type AvailableCreativeStatus = Extract<CreativeStatus, (typeof AVAILABLE_CREATIVE_STATUSES)[number]>;

const isAvailableStatus = (status: CreativeStatus): status is AvailableCreativeStatus =>
  AVAILABLE_CREATIVE_STATUSES.includes(status as AvailableCreativeStatus);

type Status = Exclude<ResellerMediaOwnersCreativeStatusResponse, 'status'> & {
  status: AvailableCreativeStatus;
};

const ResellerCreativeStatus: FunctionComponent<{
  marketId: string;
  statusCode: string;
  value?: string;
  portalAttachId?: string;
}> = ({ value, marketId, statusCode, portalAttachId }) => {
  const { localeCode } = useSelector((state: RootState) => state.publisher.configuration);
  const moderationLength = useSelector((state: RootState) => state.creative.moderation.length);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [yPosition, setYPosition] = useState(42);
  const [xPosition, setXPosition] = useState(0);
  const isDisabled = statuses.length === 0;
  const portalAttachElement = portalAttachId ? document.getElementById(portalAttachId) : null;

  const findButtonPosition = (element: Element): DOMRect => {
    if (element.tagName === 'BUTTON') {
      return element.getBoundingClientRect();
    }
    return findButtonPosition((element as Element).parentNode! as Element);
  };

  const configurePopover = (e: React.MouseEvent): void => {
    setIsDropDownOpen(!isDropDownOpen);
    if (portalAttachId && portalAttachElement) {
      const { bottom, left } = findButtonPosition(e.target as Element);
      const portalRect = portalAttachElement.getBoundingClientRect();

      setYPosition(bottom - portalRect.top + portalAttachElement.scrollTop);
      setXPosition(left - portalRect.left + portalAttachElement.scrollLeft);
    }
  };

  const statusCount: Record<SummaryCreativeStatus, number> = statuses?.reduce(
    (acc, cur) => {
      if (cur.status !== CreativeStatus.APPROVED && cur.status !== CreativeStatus.REJECTED) {
        acc[CreativeStatus.PENDING] += 1;
      } else if (cur.status === CreativeStatus.APPROVED) {
        acc[CreativeStatus.APPROVED] += 1;
      } else {
        acc[CreativeStatus.REJECTED] += 1;
      }
      return acc;
    },
    { [CreativeStatus.PENDING]: 0, [CreativeStatus.APPROVED]: 0, [CreativeStatus.REJECTED]: 0 },
  );

  const statusConfigs: Record<AvailableCreativeStatus, { bgColor: Color; icon: string }> = {
    [CreativeStatus.PENDING]: { bgColor: Color.GREY, icon: clockSvg },
    [CreativeStatus.APPROVED]: { bgColor: Color.GREEN, icon: checkmark },
    [CreativeStatus.REJECTED]: { bgColor: Color.RED, icon: crossSvg },
    [CreativeStatus.FLAGGED]: { bgColor: Color.YELLOW, icon: flagSvg },
  };

  const getStatusBadgeLabel = ({ status, automatic }: Status): string => {
    if (status !== CreativeStatus.REJECTED) return status;

    return automatic ? `${status} (AUTO)` : status;
  };

  const statusBadge = (mediaOwnerStatus: Status): ReactElement => {
    const { status } = mediaOwnerStatus;

    return (
      <Chip
        color={statusConfigs[status].bgColor}
        icon={statusConfigs[status].icon}
        label={getStatusBadgeLabel(mediaOwnerStatus)}
        size={Size.SMALL}
        truncateLabel={false}
      />
    );
  };

  const dropdownElement = (): ReactElement => (
    <div
      className={cx(
        'absolute z-80 w-[550px] block bg-white p-3 text-left rounded-2xl mt-2 shadow-[0px_0px_0px_1px_rgba(0,0,0,0.02),0px_1px_1px_-0.5px_rgba(0,0,0,0.02),0px_3px_3px_-1.5px_rgba(0,0,0,0.02),_0px_6px_6px_-3px_rgba(0,0,0,0.02),0px_12px_12px_-6px_rgba(0,0,0,0.02),0px_24px_24px_-12px_rgba(0,0,0,0.02)]',
      )}
      style={{ top: `${yPosition}px`, left: `${xPosition}px` }}
    >
      <p className="text-essential-primary sub-header-lg px-2 pb-2">Creative ID {marketId}</p>
      <p className="text-essential-tertiary sub-header-base px-2 pb-2">Approval status</p>
      <table className="shadow-none">
        <tbody>
          <tr className="border-b sub-header-sm text-essential-tertiary">
            <th className="p-2">Media owner</th>
            <th className="p-2">Status</th>
            <th className="p-2">Updated XXX</th>
          </tr>
          {statuses.map((mediaOwnerStatus) => (
            <tr key={mediaOwnerStatus.updateStamp}>
              <td className="border-r w-1/3 p-2">{mediaOwnerStatus.publisher}</td>
              <td className="border-r w-1/3 p-2">{statusBadge(mediaOwnerStatus)}</td>
              <td className="p-2">{formatDateTimeNoUTC(mediaOwnerStatus.updateStamp, localeCode)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  useEffect(() => {
    if (marketId) {
      (async () => {
        try {
          const response = await getResellerMediaOwnersCreativeStatus(marketId, {});
          const availableStatuses = response.filter((item): item is Status => isAvailableStatus(item.status));
          setStatuses(availableStatuses);
        } catch {
          setStatuses([]);
        }
      })();
    }
  }, [marketId, statusCode, moderationLength]);

  if (value === CreativeStatus.REJECTED || value === CreativeStatus.PENDING) {
    const configStatuses: Partial<Record<AvailableCreativeStatus, { color: Color; name: string }>> = {
      [CreativeStatus.PENDING]: { color: Color.YELLOW, name: 'Pending' },
      [CreativeStatus.REJECTED]: { color: Color.RED, name: 'Rejected' },
    };

    return (
      <div className="flex items-center justify-center w-44">
        <Chip label={configStatuses[value]?.name} color={configStatuses[value]?.color} size={Size.MEDIUM} />
      </div>
    );
  }

  return (
    <div
      className="relative block h-full overflow-visible"
      onBlur={() => setIsDropDownOpen(false)}
      data-test-id="reseller-creative-status"
    >
      <button
        type="button"
        className={cx('border-dashed border-2 rounded-xl flex relative', {
          'bg-primary-300 border-primary-400': isDropDownOpen,
          'bg-slate-50 border-neutral-300': !isDropDownOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          configurePopover(e);
        }}
        disabled={isDisabled}
      >
        <div className="dashed p-1 flex items-center">
          <span className="mr-2 ml-2 inline-block align-middle">
            {!isDisabled ? (
              <SVG
                src={chevron_down}
                className={cx('w-4 h-4 transition-all', {
                  'transform rotate-180': isDropDownOpen,
                  'transform rotate-0': !isDropDownOpen,
                })}
              />
            ) : (
              <div className="w-4 h-4" />
            )}
          </span>
          {SUMMARY_CREATIVE_STATUSES.map((status: SummaryCreativeStatus) => (
            <span className="mr-1" key={status}>
              <Chip
                color={statusConfigs[status as AvailableCreativeStatus].bgColor}
                icon={statusConfigs[status].icon}
                label={String(statusCount[status])}
                size={Size.MEDIUM}
                dataTestId={`status-count-${status}`}
                truncateLabel={false}
              />
            </span>
          ))}
        </div>
      </button>
      {isDropDownOpen &&
        (portalAttachElement ? createPortal(dropdownElement(), portalAttachElement) : dropdownElement())}
    </div>
  );
};

export default ResellerCreativeStatus;
/* eslint-enable sonarjs/cognitive-complexity */
